import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CogIcon, ShieldCheckIcon, BellIcon, UserIcon, BuildingOfficeIcon,
  PlusIcon, MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon,
  DocumentArrowDownIcon, ExclamationTriangleIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import settingsService from '../../services/settingsService';
import SettingsList from './SettingsList';
import SettingsForm from './SettingsForm';
import SettingsBulkUpdate from './SettingsBulkUpdate';

const SettingsManagement = () => {
  const [settings, setSettings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, [selectedCategory, searchTerm, pagination.page]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        category: selectedCategory !== 'all' ? selectedCategory : ''
      };

      const response = await settingsService.getSettings(params);
      setSettings(response.data.settings);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await settingsService.getCategories();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleCreateSetting = () => {
    setEditingSetting(null);
    setShowForm(true);
  };

  const handleEditSetting = (setting) => {
    setEditingSetting(setting);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSetting(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSetting(null);
    loadSettings();
    toast.success('Setting saved successfully');
  };

  const handleDeleteSetting = async (id) => {
    if (window.confirm('Are you sure you want to delete this setting?')) {
      try {
        await settingsService.deleteSetting(id);
        loadSettings();
        toast.success('Setting deleted successfully');
      } catch (error) {
        console.error('Error deleting setting:', error);
        toast.error('Failed to delete setting');
      }
    }
  };

  const handleBulkUpdate = async (updates) => {
    try {
      const response = await settingsService.bulkUpdateSettings(updates);
      loadSettings();
      toast.success(`Updated ${response.data.updated.length} settings`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} settings failed to update`);
      }
    } catch (error) {
      console.error('Error bulk updating settings:', error);
      toast.error('Failed to bulk update settings');
    }
  };

  const handleInitializeSettings = async () => {
    if (window.confirm('This will initialize default system settings. Continue?')) {
      try {
        const response = await settingsService.initializeSettings();
        loadSettings();
        toast.success(`Initialized ${response.data.created.length} new settings`);
      } catch (error) {
        console.error('Error initializing settings:', error);
        toast.error('Failed to initialize settings');
      }
    }
  };

  const handleExportSettings = async () => {
    try {
      const response = await settingsService.getSettings({ limit: 1000 });
      const dataStr = JSON.stringify(response.data.settings, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'settings-export.json';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Settings exported successfully');
    } catch (error) {
      console.error('Error exporting settings:', error);
      toast.error('Failed to export settings');
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePaginationChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'security':
        return ShieldCheckIcon;
      case 'company':
        return BuildingOfficeIcon;
      case 'notifications':
        return BellIcon;
      case 'user':
        return UserIcon;
      default:
        return CogIcon;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'security':
        return 'bg-red-100 text-red-600';
      case 'company':
        return 'bg-blue-100 text-blue-600';
      case 'notifications':
        return 'bg-yellow-100 text-yellow-600';
      case 'user':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CogIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">Settings & Configuration</h1>
                <p className="text-gray-600">Manage system settings and preferences</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleInitializeSettings}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Initialize
              </button>
              <button
                onClick={handleExportSettings}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowBulkUpdate(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Bulk Update
              </button>
              <button
                onClick={handleCreateSetting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Setting
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search settings..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="md:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh */}
            <button
              onClick={loadSettings}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Settings List */}
        <SettingsList
          settings={settings}
          loading={loading}
          pagination={pagination}
          onEdit={handleEditSetting}
          onDelete={handleDeleteSetting}
          onPaginationChange={handlePaginationChange}
          getCategoryIcon={getCategoryIcon}
          getCategoryColor={getCategoryColor}
        />

        {/* Settings Form Modal */}
        {showForm && (
          <SettingsForm
            setting={editingSetting}
            categories={categories}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />
        )}

        {/* Bulk Update Modal */}
        {showBulkUpdate && (
          <SettingsBulkUpdate
            settings={settings}
            onClose={() => setShowBulkUpdate(false)}
            onUpdate={handleBulkUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsManagement; 